<template>
    <DsAnchorElement
        class="ds-link-back"
        :class="computedClass"
        :url="url"
        nuxt
        @link-clicked="(e) => $emit('link-clicked', {...e, label: text })">
        <DsTextIconElement
            :icon-left="computedIcon"
            :text="text"/>
    </DsAnchorElement>
</template>

<script>
import { DsAnchorElement, DsTextIconElement } from '../../atoms';

/**
 * Link Clicked event
 * @event link-clicked
 * @type {object}
 * @property {String} url - link url
 * @property {String} label - link text
 */

/**
 * DsLinkBack
 *
 * @emits link-clicked
 *
 * @example
 * ```js
 * import import DsLinkBack from '../../elements/link-back/link-back';
 *
 * export default {
 *   components: { DsLinkBack },
 *   template:`
 *    <DsLinkBack
 *       :text="text"
 *       :type="type"
 *       :url="url"
 *       color="black"
 *       @link-clicked="handleLinkClicked"
 *    />`
 *  }
 *```
 */
export default {
    name: 'DsLinkBack',
    components: { DsAnchorElement, DsTextIconElement },
    props: {
        text: {
            type: String,
            required: true
        },
        type: {
            type: String,
            default: 'primary',
            validator: value => ['primary', 'secondary'].includes(value)
        },
        color: {
            type: String,
            default: 'green',
            validator: value => ['green', 'black'].includes(value)
        },
        url: {
            type: String,
            required: true
        }
    },
    computed: {
        computedClass () {
            return this.type === 'primary' ? `ds-link-back--primary ds-link-back--primary--${this.color}` : 'ds-link-back--secondary';
        },
        computedIcon () {
            if (this.type === 'primary') {
                return { name: 'arrow-left', category: 'arrows' };
            }
            if (this.type === 'secondary') {
                return { name: 'chevron-left-small', category: 'arrows' };
            }
            return null;
        }
    }
};
</script>

<style lang="scss" scoped>
@import '../../foundation/tokens/variables';
@import '../../foundation/mixins/buttons/button.mixins';
@import '../../foundation/mixins/buttons/button-link-states.mixins';

@mixin ds-link-back {
    @include button-base;

    align-items: center;
    text-align: left;

    :deep(.icon) {
        flex: none;
        transition: transform 0.15s ease-out;
    }

    @media (hover: hover) {
        &:hover {
            :deep(.icon) {
                transform: translate(-2px, 0);
            }
        }
    }
}

@mixin link-back--primary {
    :deep(.icon) {
        margin-right: $spacing-12;
    }
}

@mixin link-back--primary--green {
    @include button-link-states(
        $color-green, // text-color
        $color-green-500, // text-hover-color
        false, // text-hover-underline
        $color-green-200 // border-focus-color
    );
}

@mixin link-back--primary--black {
    @include button-link-states(
        $color-black, // text-color
        $color-grey-600, // text-hover-color
        false, // text-hover-underline
        $color-green-200 // border-focus-color
    );
}

@mixin link-back--secondary {
    font-family: $font-family-for-small-text;
    font-size: $font-size-small;
    font-weight: $font-weight;
    line-height: $line-height-small;

    @include button-link-states(
        $color-grey-450, // text-color
        $color-grey-600, // text-hover-color
        false, // text-hover-underline
        $color-green-200 // border-focus-color
    );
}

.ds-link-back {
    @include ds-link-back;
    &--primary { @include link-back--primary; }
    &--primary--green { @include link-back--primary--green; }
    &--primary--black { @include link-back--primary--black; }
    &--secondary { @include link-back--secondary; }
}
</style>
